import {
    AccountBalanceWalletOutlined,
    AdminPanelSettingsOutlined,
    AnalyticsOutlined,
    AutoAwesomeOutlined,
    BusinessOutlined,
    Close as CloseIcon,
    DashboardOutlined,
    FolderCopyOutlined,
    InsightsOutlined,
    KeyboardArrowDownOutlined,
    LogoutOutlined,
    PersonOutlined,
    TimelineOutlined,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Location, useNavigate } from 'react-router-dom';

import AccountSwitcher from 'components/v2/AccountSwitcher';
import { useWorkspace } from 'stores/workspaceStore';

import { isMainNavActive } from 'utils/isMainNavActive';

import PERMISSION from 'assets/permissions.json';
import ROLES from 'assets/userRoles.json';
import { FEATURE } from 'constant/features';
import { ADMIN_ROLES } from 'data/constants/roles';
import { useHasFeature } from 'hooks/useHasFeature';
import { useProfilePicture } from 'hooks/useMyAccount';
import useTranslate from 'hooks/useTranslate';
import { logout } from 'redux/auth/auth-middleware';
import { AppDispatch, RootState } from 'redux/store';
import { MainLink } from 'types';

export interface BurgerMenuProps {
    /** Optional override; falls back to Redux / profile_picture API */
    avatar?: string;
    userName?: string;
    links: MainLink[];
    location: Location;
    userRole: string;
}

interface AccountNavLink {
    icon: React.ElementType;
    title: string;
    path?: string;
    allowedFor: string[];
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ avatar, userName, links, location, userRole }) => {
    const userEntity = useSelector((state: RootState) => state.user.entity);
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { translate } = useTranslate();
    const { toggleLayoutMode } = useWorkspace();
    // Same source as ProfileAvatar — react-query cache shared app-wide
    const { data: profilePictureUrl } = useProfilePicture();
    const { service_provider_id, email: userEmail } = userEntity;
    const avatarSrc = profilePictureUrl || avatar || undefined;
    const avatarInitial =
        (userName && userName.trim().charAt(0).toUpperCase()) ||
        userEntity.firstname?.charAt(0)?.toUpperCase() ||
        '?';
    const displayEmail =
        typeof userEmail === 'string' && userEmail.trim().length > 0 ? userEmail.trim() : undefined;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    // App build version from public/version.json (also cached by checkForVersionUpdates).
    const [appVersion, setAppVersion] = useState(() => localStorage.getItem('app_version') || '');
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (appVersion) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch('/version.json', { cache: 'no-cache' });
                const data = (await response.json()) as { version?: string };
                if (!cancelled && data?.version) {
                    setAppVersion(data.version);
                }
            } catch {
                // Version label is non-critical; leave blank if unavailable.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [appVersion]);

    const canEditOrganizationProfile =
        !!service_provider_id &&
        permissionsList.some(
            (permission) => permission?.name === PERMISSION.EDIT_ORGANIZATION_PROFILE
        );
    const hasFinancials = useHasFeature(FEATURE.FINANCIALS);

    // Absolute paths so navigation works from any current route (not relative to /dashboard/…).
    // Change Password, My Licenses, and My Signatures live on the My Account page, not as extra rows here.
    const accountNavLinks: AccountNavLink[] = [
        {
            icon: PersonOutlined,
            title: translate('myAccount'),
            path: '/account/my-account',
            allowedFor: Object.values(ROLES),
        },
        ...(canEditOrganizationProfile
            ? [
                  {
                      icon: BusinessOutlined,
                      title: translate('companyProfile'),
                      path: `/dashboard/${service_provider_id}/organization`,
                      allowedFor: Object.values(ROLES),
                  },
              ]
            : []),
        {
            icon: AdminPanelSettingsOutlined,
            title: translate('permissions'),
            path: '/account/permissions',
            allowedFor: ADMIN_ROLES,
        },
        {
            icon: FolderCopyOutlined,
            title: translate('servicesDocuments.menuItem'),
            path: '/account/services-documents',
            allowedFor: ADMIN_ROLES,
        },
        ...(hasFinancials
            ? [
                  {
                      icon: AccountBalanceWalletOutlined,
                      title: translate('financials'),
                      path: '/account/financials',
                      allowedFor: ADMIN_ROLES,
                  },
              ]
            : []),
        {
            icon: AnalyticsOutlined,
            title: translate('analytics'),
            path: '/account/analytics',
            allowedFor: ADMIN_ROLES,
        },
        {
            icon: InsightsOutlined,
            title: translate('adminAnalytics'),
            path: '/account/admin-analytics/overview',
            allowedFor: [ROLES.superSuperAdmin],
        },
        {
            icon: DashboardOutlined,
            title: translate('adminPanel'),
            path: '/account/admin-panel',
            allowedFor: [ROLES.superSuperAdmin],
        },
        {
            icon: TimelineOutlined,
            title: translate('eventTracker'),
            path: '/account/event-tracker',
            allowedFor: [ROLES.superSuperAdmin],
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (userRole.length > 0) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const closePopoverWithDelay = () => {
        setTimeout(() => {
            setAnchorEl(null);
        }, 300);
    };

    const handleSignOut = async () => {
        await dispatch(logout());
        navigate('/login');
        closePopoverWithDelay();
    };

    const avatarAlt = translate('avatar');
    const mainNavPaths = links.map((link) => link.path);

    /* ── Shared menu content (used in both Popover & Drawer) ── */
    const menuContent = (
        <Box
            component="nav"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            {/* ── Profile header (fixed) ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 2,
                    pt: isMobile ? 1.5 : 1.75,
                    pb: 1.5,
                    flexShrink: 0,
                }}
            >
                <Avatar
                    key={avatarSrc || 'avatar-fallback'}
                    src={avatarSrc}
                    alt={avatarAlt}
                    sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        bgcolor: avatarSrc ? 'transparent' : 'primary.main',
                        fontSize: 16,
                        fontWeight: 600,
                    }}
                >
                    {!avatarSrc ? avatarInitial : null}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    {userName && (
                        <Typography
                            title={userName}
                            sx={{
                                fontWeight: 700,
                                fontSize: 14,
                                lineHeight: 1.3,
                                color: 'text.primary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {userName}
                        </Typography>
                    )}
                    {displayEmail && (
                        <Typography
                            title={displayEmail}
                            sx={{
                                fontSize: 12,
                                lineHeight: 1.3,
                                color: 'text.secondary',
                                mt: 0.15,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {displayEmail}
                        </Typography>
                    )}
                    <Typography
                        sx={{
                            fontSize: 11,
                            color: 'text.secondary',
                            textTransform: 'capitalize',
                            mt: 0.25,
                        }}
                    >
                        {userRole.replace(/_/g, ' ')}
                    </Typography>
                </Box>
                {isMobile && (
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            ml: 'auto',
                            color: 'text.secondary',
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Divider sx={{ flexShrink: 0 }} />

            <AccountSwitcher />

            {/* ── Scrollable nav lists (main + account) ── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {/* ── Main nav links — mobile only ── */}
                {isMobile && links.length > 0 && (
                    <>
                        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: 'text.disabled',
                                    px: 1.5,
                                    mb: 0.5,
                                }}
                            >
                                {translate('navigation')}
                            </Typography>
                            <List disablePadding>
                                {links.map((link) => {
                                    const isActive = isMainNavActive(
                                        location.pathname,
                                        link.path,
                                        mainNavPaths
                                    );
                                    return (
                                        <ListItemButton
                                            key={link.name}
                                            onClick={() => {
                                                navigate(link.path);
                                                closePopoverWithDelay();
                                            }}
                                            selected={isActive}
                                            sx={{
                                                borderRadius: 1.5,
                                                mb: 0.15,
                                                py: 0.5,
                                                px: 1.25,
                                                minHeight: 34,
                                                textTransform: 'capitalize',
                                                '&.Mui-selected': {
                                                    bgcolor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                    '&:hover': {
                                                        bgcolor: 'primary.dark',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={link.name}
                                                slotProps={{
                                                    primary: {
                                                        sx: {
                                                            fontSize: 13,
                                                            fontWeight: isActive ? 600 : 500,
                                                        },
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Box>
                        <Divider />
                    </>
                )}

                {/* ── Account section ── */}
                <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'text.disabled',
                            px: 1.5,
                            mb: 0.5,
                        }}
                    >
                        {translate('myAccount')}
                    </Typography>
                    <List disablePadding>
                        {accountNavLinks.map((item, i) => {
                            if (!item.allowedFor.includes(userRole)) return null;
                            const Icon = item.icon;
                            const itemPath = item.path ?? '';
                            const isActive = itemPath
                                ? location.pathname.includes(itemPath)
                                : false;
                            return (
                                <ListItemButton
                                    key={i}
                                    onClick={() => {
                                        if (itemPath) navigate(itemPath);
                                        closePopoverWithDelay();
                                    }}
                                    selected={isActive}
                                    sx={{
                                        borderRadius: 1.5,
                                        mb: 0.15,
                                        py: 0.5,
                                        px: 1.25,
                                        minHeight: 34,
                                        '&.Mui-selected': {
                                            bgcolor: 'action.selected',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 30,
                                            color: isActive ? 'primary.main' : 'text.secondary',
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 18 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.title}
                                        slotProps={{
                                            primary: {
                                                sx: {
                                                    fontSize: 13,
                                                    fontWeight: isActive ? 600 : 400,
                                                    color: isActive
                                                        ? 'primary.main'
                                                        : 'text.primary',
                                                },
                                            },
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Box>
            </Box>

            <Divider sx={{ flexShrink: 0 }} />

            {/* ── Footer actions (fixed) ── */}
            <Box sx={{ px: 1, py: 0.75, flexShrink: 0 }}>
                {userRole === ROLES.externalClient && (
                    <ListItemButton
                        onClick={() => {
                            toggleLayoutMode();
                            closePopoverWithDelay();
                        }}
                        sx={{
                            borderRadius: 1.5,
                            py: 0.5,
                            px: 1.25,
                            minHeight: 34,
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 30, color: 'text.secondary' }}>
                            <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={translate('betaLayout')}
                            slotProps={{
                                primary: {
                                    sx: {
                                        fontSize: 13,
                                        fontWeight: 400,
                                    },
                                },
                            }}
                        />
                    </ListItemButton>
                )}
                <ListItemButton
                    onClick={handleSignOut}
                    sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.25,
                        minHeight: 34,
                        color: 'error.main',
                        '&:hover': {
                            bgcolor: 'action.hover',
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 30, color: 'error.main' }}>
                        <LogoutOutlined sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={translate('logOut')}
                        slotProps={{
                            primary: {
                                sx: {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'error.main',
                                },
                            },
                        }}
                    />
                </ListItemButton>

                {appVersion ? (
                    <Typography
                        component="p"
                        sx={{
                            my: 0,
                            px: 1.5,
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: '0.04em',
                            color: 'text.disabled',
                            textAlign: 'center',
                            userSelect: 'none',
                        }}
                    >
                        v{appVersion}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* ── Avatar trigger (pill / fully rounded chip) ── */}
            <Box
                onClick={handleClick}
                role="button"
                aria-haspopup="menu"
                aria-expanded={open ? 'true' : undefined}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    height: 36,
                    py: 0,
                    pl: 0.5,
                    pr: isMobile ? 0.5 : 1.25,
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    bgcolor: open ? 'action.selected' : 'transparent',
                    '&:hover': {
                        bgcolor: 'action.hover',
                    },
                }}
            >
                <Avatar
                    key={avatarSrc || 'avatar-fallback'}
                    src={avatarSrc}
                    alt={avatarAlt}
                    sx={{
                        width: 28,
                        height: 28,
                        bgcolor: avatarSrc ? 'transparent' : 'primary.main',
                        fontSize: 12,
                        fontWeight: 600,
                        flexShrink: 0,
                    }}
                >
                    {!avatarSrc ? avatarInitial : null}
                </Avatar>
                {!isMobile && userName && (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: 12.5,
                            lineHeight: 1.2,
                            letterSpacing: '-0.01em',
                            color: 'text.primary',
                            maxWidth: 120,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {userName}
                    </Typography>
                )}
                {!isMobile && (
                    <KeyboardArrowDownOutlined
                        sx={{
                            fontSize: 16,
                            color: 'text.secondary',
                            flexShrink: 0,
                            transition: 'transform 0.15s ease',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                )}
            </Box>

            {/* ── Desktop: Popover dropdown ── */}
            {!isMobile && (
                <Popover
                    disableScrollLock
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    slotProps={{
                        paper: {
                            elevation: 0,
                            sx: {
                                mt: 1.5,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 3,
                                minWidth: 280,
                                maxWidth: 320,
                                // Cap height so short viewports can scroll the list
                                maxHeight: 'min(560px, calc(100vh - 96px))',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backdropFilter: 'blur(20px)',
                            },
                        },
                    }}
                >
                    {menuContent}
                </Popover>
            )}

            {/* ── Mobile: Drawer slide-in ── */}
            {isMobile && (
                <Drawer
                    anchor="right"
                    open={open}
                    onClose={handleClose}
                    sx={{ zIndex: (muiTheme) => muiTheme.zIndex.modal + 999999 }}
                    slotProps={{
                        paper: {
                            sx: {
                                width: '85vw',
                                maxWidth: 360,
                                height: '100%',
                                maxHeight: '100dvh',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                borderRadius: '16px 0 0 16px',
                                boxShadow: 3,
                            },
                        },
                    }}
                >
                    {menuContent}
                </Drawer>
            )}
        </Box>
    );
};

export default BurgerMenu;
